import List, { ListItem } from '@/common/components/List/List';
import { Nullable } from '@/common/types';
import { useMemo, useState } from 'react';
import ManageLanguageDrawer from './ManageDrawers/ManageLanguageDrawer';
import ManageUnitDrawer from './ManageDrawers/ManageUnitDrawer';
import ManageDevicePreferenceDrawer from './ManageDrawers/ManageDevicePreferenceDrawer';
import ManageAlarmIdDrawer from './ManageDrawers/ManageAlarmIdDrawer';
import { useAppSelector } from '@/reducers/rootReducer';
import { translationSelectors } from '@/reducers/translationSlice';
import { userDetailsSelectors } from '@/reducers/userDetailsSlice';
import { Box } from '@mui/material';
import { lelyTokenPrimitives } from '@/common/styles/newStyles/LelyMuiTheme';

const t = lelyTokenPrimitives as Record<string, string>;

export const SETTING_TYPES = {
  Language: 'Language',
  Unit: 'Unit',
  DevicePreference: 'Device preference',
  AlarmId: 'Alarm ID',
} as const;

export type SettingType = (typeof SETTING_TYPES)[keyof typeof SETTING_TYPES];

export type SettingsDrawerProps = {
  open: boolean;
  onClose: () => void;
  onCloseAll: () => void;
};

type ManageSettingsProps = {
  onCloseUserSettings: () => void;
};

const ManageSettings = ({ onCloseUserSettings }: ManageSettingsProps) => {
  // No duplication of store values into local state. The selected values are coming from the store
  // The inner Manage drawer should update the selected option via the store action.
  const language = useAppSelector(
    translationSelectors.selectPreferredLanguageName,
  );
  const alarmId = useAppSelector(
    userDetailsSelectors.selectAlarmIdRepresentation,
  );
  const unit = useAppSelector(userDetailsSelectors.selectUnitPreference);

  const [activeSettingDrawer, setActiveSettingDrawer] =
    useState<Nullable<SettingType>>();

  const resetDrawer = () => setActiveSettingDrawer(null);
  const closeAll = () => {
    resetDrawer();
    onCloseUserSettings();
  };

  const userSettingsItems: ListItem[] = useMemo(
    () => [
      {
        primary: SETTING_TYPES.Language,
        secondary: language,
        onClick: () => setActiveSettingDrawer('Language'),
      },

      {
        primary: SETTING_TYPES.Unit,
        secondary: unit,
        onClick: () => setActiveSettingDrawer('Unit'),
      },
    ],
    [language, unit, setActiveSettingDrawer],
  );

  const appSettingsItems: ListItem[] = useMemo(
    () => [
      {
        primary: SETTING_TYPES.DevicePreference,
        secondary: 'Determine which devices are visible',
        onClick: () => setActiveSettingDrawer('Device preference'),
      },

      {
        primary: SETTING_TYPES.AlarmId,
        secondary: alarmId,
        onClick: () => setActiveSettingDrawer('Alarm ID'),
      },
    ],
    [alarmId],
  );

  return (
    <>
      <Box py={t.spaceSm}>
        <List
          subheader="User settings"
          type="selectable"
          items={userSettingsItems}
        />
        <List
          subheader="App settings"
          type="selectable"
          items={appSettingsItems}
        />
      </Box>

      <ManageLanguageDrawer
        open={activeSettingDrawer === 'Language'}
        onClose={resetDrawer}
        onCloseAll={closeAll}
      />
      <ManageUnitDrawer
        open={activeSettingDrawer === 'Unit'}
        onClose={resetDrawer}
        onCloseAll={closeAll}
      />
      <ManageDevicePreferenceDrawer
        open={activeSettingDrawer === 'Device preference'}
        onClose={resetDrawer}
        onCloseAll={closeAll}
      />
      <ManageAlarmIdDrawer
        open={activeSettingDrawer === 'Alarm ID'}
        onClose={resetDrawer}
        onCloseAll={closeAll}
      />
    </>
  );
};

export default ManageSettings;
